import { DEFAULT_AVATAR } from '../types';

/**
 * 서버 절대 URL 해석 — 이 앱에서 "서버 주소를 앞에 붙이는" 유일한 출처.
 *
 * 서버는 업로드물(첨부·아바타)을 루트 상대경로(`/uploads/...`)로 저장하고 응답한다.
 * 웹은 프론트와 API가 같은 origin이라 상대경로가 그대로 맞지만, Tauri 데스크톱은
 * 화면 origin이 `tauri.localhost`(로컬 번들)라서 상대경로가 앱 내부를 가리켜 404가 난다.
 * 그래서 <img src>·다운로드 링크에 넣기 전에 반드시 여기를 거쳐야 한다.
 *
 * 예전에는 이 계산이 apiClient·authService·socketService·ChatWindow·AttachmentDrawer
 * 5곳에 복제돼 있었고, 아바타 렌더 지점에는 아예 빠져 있어 데스크톱에서 업로드한
 * 프로필 사진이 항상 기본 아바타로 폴백됐다(2026-08 실제 발생).
 *
 * ⚠ `process.env.REACT_APP_SERVER_URL`은 반드시 이 형태의 리터럴로 둔다 —
 *   CRA(webpack DefinePlugin)가 빌드 시 문자열로 치환하므로 동적 접근은 undefined가 된다.
 *   데스크톱 빌드는 `scripts/build-affiliates.js`가 계열사 도메인을 여기에 주입한다.
 */

// 현재 페이지의 프로토콜에 맞춰 API 프로토콜 결정
// 표준 포트(80/443)로 접속 시 역방향 프록시 경유 → origin 사용
// 그 외(직접 접속, Tauri, CRA dev)에서는 :15000 사용
const protocol = window.location.protocol === 'http:' ? 'http' : 'https';
const hostname = window.location.hostname || 'localhost';
const port = window.location.port;
const isStandardPort = !port || port === '80' || port === '443';

/** 서버 기본 URL — Tauri는 빌드 시 주입된 REACT_APP_SERVER_URL, 웹은 현재 origin */
export const SERVER_BASE_URL: string =
  process.env.REACT_APP_SERVER_URL ||
  (isStandardPort ? window.location.origin : `${protocol}://${hostname}:15000`);

/**
 * 서버가 준 경로를 절대 URL로 바꾼다. **서버 루트 상대경로(`/...`)만** 대상이다.
 *
 * `//host/x.png`(프로토콜 상대 URL)는 `startsWith('/')`가 참이지만 결합하면
 * `https://chat.inaom.co.kr//host/x.png`라는 엉뚱한 주소가 된다. 아바타는 '직접 URL 입력'으로
 * 사용자 입력이 검증 없이 들어오는 경로가 있어 실제로 닿을 수 있으므로 따로 제외한다.
 * `http(s)://`·`data:`·`blob:`는 애초에 `/`로 시작하지 않아 자연히 그대로 통과한다
 * (첨부의 `Attachment.url`은 base64 data URL일 수 있다 — types.ts 참조).
 */
export function resolveServerUrl(url: string): string {
  if (!url.startsWith('/') || url.startsWith('//')) return url;
  return `${SERVER_BASE_URL}${url}`;
}

/**
 * 아바타 <img src>는 반드시 이 함수를 통과한다 — 앱 전역 규칙, 예외 없음.
 *
 * `user_profiles.avatar_url`에는 세 종류가 섞여 있다: DiceBear 프리셋(https://…),
 * 사용자가 넣은 외부 URL, 직접 올린 사진(`/uploads/avatars/<uuid>.jpg` 상대경로).
 *
 * 렌더 지점이 쓰던 `X || DEFAULT_AVATAR` 폴백을 여기서 흡수한다 — 호출부가 폴백을 잊는 사고
 * (IncomingCallBanner가 실제로 그랬다)와 `src=""`(브라우저가 현재 페이지를 다시 요청한다)를
 * 구조적으로 막는다. data:/blob:/http(s)는 그대로 통과하므로 멱등이다
 * (이미 DEFAULT_AVATAR가 담긴 값을 다시 넣어도 안전 — App.tsx의 senderAvatar 정규화).
 *
 * ⚠ 반환값(절대 URL)을 state·localStorage·소켓·DB에 저장하지 말 것. 서버 주소는 계열사
 *   빌드마다 다르므로, 저장하면 한 클라이언트의 주소가 다른 클라이언트로 전파된다.
 */
export function resolveAvatarUrl(url?: string | null): string {
  const v = url?.trim();
  if (!v) return DEFAULT_AVATAR;
  return resolveServerUrl(v);
}
